import { useMemo, useState } from "react";
import moment from "moment";
import { inr } from "./AffiliateLayout";

interface CommissionChartProps {
  // charts.daily from /affiliate/affiliate-commission-list: [{ date: "YYYY-MM-DD", total }]
  daily: { date: string; total: number }[];
  // selected period (null = all time → first to last commission)
  from: moment.Moment | null;
  to: moment.Moment | null;
}

const W = 640, H = 190, TOP = 12, BOTTOM = 22, BAR_MAX = 24;

interface ChartPoint {
  label: string;
  full: string;
  value: number;
}

// Commission per day (per month when the range is longer than ~2 months), single series
const CommissionChart = ({ daily, from, to }: CommissionChartProps) => {
  const [hover, setHover] = useState<number | null>(null);

  // Fill the empty days / months so the bars sit on a real time axis
  const { points, monthly } = useMemo((): { points: ChartPoint[]; monthly: boolean } => {
    if (!daily.length && (!from || !to)) return { points: [], monthly: false };
    const byDate = new Map(daily.map(d => [d.date, Number(d.total) || 0]));
    const start = (from ? from.clone() : moment(daily[0].date)).startOf("day");
    const end = moment.min(to ? to.clone() : moment(daily[daily.length - 1].date), moment()).startOf("day");
    if (end.isBefore(start)) return { points: [], monthly: false };

    if (end.diff(start, "days") < 62) {
      const out: ChartPoint[] = [];
      for (const d = start.clone(); !d.isAfter(end); d.add(1, "day")) {
        out.push({ label: d.format("DD MMM"), full: d.format("DD MMM YYYY"), value: byDate.get(d.format("YYYY-MM-DD")) || 0 });
      }
      return { points: out, monthly: false };
    }

    const byMonth = new Map<string, number>();
    daily.forEach(d => byMonth.set(d.date.slice(0, 7), (byMonth.get(d.date.slice(0, 7)) || 0) + (Number(d.total) || 0)));
    const out: ChartPoint[] = [];
    for (const m = start.clone().startOf("month"); !m.isAfter(end); m.add(1, "month")) {
      out.push({ label: m.format("MMM YY"), full: m.format("MMMM YYYY"), value: byMonth.get(m.format("YYYY-MM")) || 0 });
    }
    return { points: out, monthly: true };
  }, [daily, from, to]);

  const max = Math.max(...points.map(p => p.value), 0);
  const total = points.reduce((s, p) => s + p.value, 0);

  if (!points.length || max === 0) {
    return <div className="cx-empty" style={{ padding: "56px 20px" }}><i className="ri-bar-chart-2-line"></i>No commission in this period.</div>;
  }

  const plotH = H - TOP - BOTTOM;
  const slot = W / points.length;
  const barW = Math.max(2, Math.min(BAR_MAX, slot * 0.7));
  // ~8 x labels at most so they never collide
  const labelEvery = Math.ceil(points.length / 8);
  const hp = hover !== null ? points[hover] : null;
  const hx = hover !== null ? ((hover + 0.5) * slot / W) * 100 : 0;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 190, display: "block" }} role="img" aria-label={`Commission per ${monthly ? "month" : "day"}, total ${inr(total)}`}>
        {[0, 0.5, 1].map(f => {
          const y = TOP + plotH * (1 - f);
          return (
            <g key={f}>
              <line x1="0" x2={W} y1={y} y2={y} stroke="#eef0f3" />
              {f > 0 && <text x="2" y={y - 4} fontSize="10" fill="#8a909c" fontFamily="JetBrains Mono">{inr(max * f, 0)}</text>}
            </g>
          );
        })}
        {points.map((p, i) => {
          const bh = p.value > 0 ? Math.max(2, (p.value / max) * plotH) : 0;
          const x = i * slot + (slot - barW) / 2;
          const y = TOP + plotH - bh;
          const r = Math.min(4, barW / 2, bh);
          return (
            <g key={i}>
              {bh > 0 && (
                // rounded data end, square on the baseline
                <path
                  d={`M${x},${y + bh} V${y + r} Q${x},${y} ${x + r},${y} H${x + barW - r} Q${x + barW},${y} ${x + barW},${y + r} V${y + bh} Z`}
                  fill={hover === i ? "#032e67" : "#88b3ed"}
                />
              )}
              {i % labelEvery === 0 && (
                <text x={i * slot + slot / 2} y={H - 6} textAnchor="middle" fontSize="10" fill="#849690" fontFamily="JetBrains Mono">{p.label}</text>
              )}
              {/* hit area = the whole column, bigger than the bar */}
              <rect
                x={i * slot} y={TOP} width={slot} height={plotH} fill="transparent"
                tabIndex={0} aria-label={`${p.full}: ${inr(p.value)}`}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                style={{ outline: "none", cursor: "default" }}
              />
            </g>
          );
        })}
      </svg>
      {hp && (
        <div
          style={{
            position: "absolute", top: 0, left: `${hx}%`, pointerEvents: "none",
            transform: `translateX(${hx < 12 ? "0" : hx > 88 ? "-100%" : "-50%"})`,
            background: "#12131c", color: "#fff", borderRadius: 7, padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap", zIndex: 2,
          }}
        >
          <b className="mono">{inr(hp.value)}</b>
          <div style={{ opacity: .7, fontSize: 11 }}>{hp.full}</div>
        </div>
      )}
    </div>
  );
};

export default CommissionChart;
